import { FC, useMemo } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { NodeSequenceType } from '@/types/workflow/nodeSequence';


interface NodeSequenceStepperProps {
    sequences: NodeSequenceType[];
}

const NodeSequenceStepper: FC<NodeSequenceStepperProps> = ({ sequences }) => {
    const grouped = useMemo(() => {
        if (!Array.isArray(sequences) || sequences.length === 0) return [];

        const groups: Record<number, NodeSequenceType[]> = {};
        sequences.forEach((item) => {
            if (!groups[item.sequence]) groups[item.sequence] = [];
            groups[item.sequence].push(item);
        });

        const sortedKeys = Object.keys(groups)
            .map(Number)
            .sort((a, b) => a - b);

        return sortedKeys.map((key) => {
            const nodes = groups[key];

            const minOrderNode = [...nodes].sort(
                (a, b) => a.orderIndex - b.orderIndex
            )[0];

            const position = nodes.some((n) => n.sequencePosition === 1)
                ? 1
                : nodes.some((n) => n.sequencePosition === 0)
                    ? 0
                    : 2;

            return { sequence: key, nodes, position, minOrderNode };
        });
    }, [sequences]);

    if (grouped.length === 0) return null;

    const getLineColor = (currentGroup: any, nextGroup: any) => {
        if (currentGroup.position === 0 && nextGroup.position === 1) {
            return 'bg-blue-500';
        }
        if (currentGroup.position === 0 && nextGroup.position === 0) {
            return 'bg-green-500';
        }
        return 'bg-gray-300';
    };

    return (
        <div className="flex items-center justify-center px-2 overflow-x-auto">
            {grouped.map((group, index) => (
                <div key={group.sequence} className="flex items-center">
                    <div className="flex flex-col items-center relative">
                        <div className="h-10 flex items-end mb-1">
                            {group.position === 1 && (
                                <div className="text-sm font-medium text-blue-600 text-center px-1 max-w-[100px] truncate">
                                    {group.minOrderNode?.nodeName}
                                </div>
                            )}
                        </div>
                        <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                ${group.position === 0
                                    ? 'bg-green-500 text-white'
                                    : ''
                                }
                ${group.position === 1
                                    ? 'border-2 border-blue-500 bg-white text-blue-500'
                                    : ''
                                }
                ${group.position === 2
                                    ? 'bg-gray-300 text-gray-700'
                                    : ''
                                }
              `}
                        >
                            {group.position === 0 && (
                                <CheckIcon className="w-5 h-5" />
                            )}
                            {group.position === 1 && group.sequence}
                            {group.position === 2 && group.sequence}
                        </div>
                        <div className="h-10 mt-1">
                            {group.position !== 1 && (
                                <div className="text-xs text-gray-600 text-center px-1 max-w-[100px] truncate">
                                    {group.minOrderNode?.nodeName}
                                </div>
                            )}
                        </div>
                    </div>
                    {index < grouped.length - 1 && (
                      <span className='text-2xl'>←</span>
                    )}
                </div>
            ))}
        </div>
    );
};

export default NodeSequenceStepper;